import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtDate, tl } from "@/lib/i18nFormat";
import { PageHeader, StatusPill, LoadingBlock } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Power, Copy, Mail } from "lucide-react";

import { useI18n } from "@/context/I18nContext";

interface UserRow {
  id: string; email: string; name: string; status: string;
  roles: string[]; membership_status: string;
}

const ROLES = [
  "tenant_admin", "corporate_exec", "regional_manager", "district_manager",
  "store_manager", "assistant_manager", "associate", "auditor",
];

export default function Users() {
  const {
    t
  } = useI18n();

  const { hasPerm } = useAuth();
  const canManage = hasPerm("users.manage");
  const [users, setUsers] = useState<UserRow[]>([]);
  const [invites, setInvites] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [tree, setTree] = useState<any>({ regions: [], districts: [], stores: [] });
  const [invite, setInvite] = useState<any>(null);

  // invite form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("associate");
  const [scopeType, setScopeType] = useState<"tenant" | "region" | "district" | "store">("store");
  const [scopeId, setScopeId] = useState<string>("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/users").then((r) => setUsers(r.data.users)).catch(() => {}),
      api.get("/admin/invitations").then((r) => setInvites(r.data.invitations || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!canManage) return;
    load();
    api.get("/org/tree").then((r) => setTree(r.data)).catch(() => {});
  }, [canManage]);

  if (!canManage) return <Navigate to="/today" replace />;

  const scopeOptions = scopeType === "region" ? tree.regions
    : scopeType === "district" ? tree.districts
    : scopeType === "store" ? tree.stores : [];

  const create = async () => {
    if (!name || !email) {
      toast.error("Name and email are required");
      return;
    }
    if (scopeType !== "tenant" && !scopeId) {
      toast.error("Select a scope");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/invitations", {
        name, email,
        assignments: [{ role, scope_type: scopeType, scope_id: scopeType === "tenant" ? null : scopeId }],
      });
      setInvite(r.data);
      setOpen(false);
      setName(""); setEmail(""); setScopeId("");
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Failed to create invitation");
    } finally {
      setBusy(false);
    }
  };

  const revokeInvite = async (id: string) => {
    try { await api.post(`/admin/invitations/${id}/revoke`); toast.success("Invitation revoked"); load(); }
    catch (err: any) { toast.error(err?.response?.data?.detail || "Revoke failed"); }
  };

  const toggleStatus = async (u: UserRow) => {
    const next = u.status === "active" ? "inactive" : "active";
    try {
      await api.patch(`/admin/users/${u.id}`, { status: next });
      toast.success(next === "active" ? tl("User activated") : tl("User deactivated"));
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  return (
    (<div>
      <PageHeader
        title="Users & Roles"
        subtitle="Manage tenant members, roles, and access scope"
        testId="users-header"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-button"><UserPlus size={16} className="mr-1.5" />{" "}{t("Invite user")}</Button>
            </DialogTrigger>
            <DialogContent data-testid="add-user-dialog">
              <DialogHeader><DialogTitle>{t("Invite user")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>{t("Name")}</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="new-user-name" className="mt-1" /></div>
                <div><Label>{t("Email")}</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="new-user-email" className="mt-1" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t("Role")}</Label>
                    <Select value={role} onValueChange={setRole}>
                      <SelectTrigger className="mt-1" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                      <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t("Scope")}</Label>
                    <Select value={scopeType} onValueChange={(v: any) => { setScopeType(v); setScopeId(""); }}>
                      <SelectTrigger className="mt-1" data-testid="new-user-scope-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tenant">{t("Whole tenant")}</SelectItem>
                        <SelectItem value="region">{t("Region")}</SelectItem>
                        <SelectItem value="district">{t("District")}</SelectItem>
                        <SelectItem value="store">{t("Store")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {scopeType !== "tenant" && (
                  <div>
                    <Label>{scopeType}{" "}{t("selection")}</Label>
                    <Select value={scopeId} onValueChange={setScopeId}>
                      <SelectTrigger className="mt-1" data-testid="new-user-scope-id"><SelectValue placeholder={`${tl("Select {scope}", { scope: scopeType })}`} /></SelectTrigger>
                      <SelectContent>
                        {scopeOptions.map((o: any) => (
                          <SelectItem key={o.id} value={o.id}>{o.name}{o.store_number ? ` (#${o.store_number})` : ""}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button onClick={create} disabled={busy} data-testid="submit-new-user">{busy ? t("Creating…") : t("Create invitation")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <Dialog open={!!invite} onOpenChange={(o) => !o && setInvite(null)}>
        <DialogContent data-testid="invite-token-dialog">
          <DialogHeader><DialogTitle>{t("Share this activation link")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t(
            "Copy and send this out-of-band. It is shown once, expires soon, and can be used only once. The invited user sets their own password."
          )}</p>
          {invite && (
            <div className="mt-2 flex items-center gap-2">
              <code className="flex-1 truncate rounded bg-muted px-2 py-1 text-xs" data-testid="invite-activation-link">{`${window.location.origin}${invite.activation_path}`}</code>
              <Button size="icon" variant="outline" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}${invite.activation_path}`); toast.success("Copied"); }}><Copy size={14} /></Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {invites.filter((i) => i.status === "pending").length > 0 && (
        <Card className="mb-4 p-4" data-testid="pending-invites">
          <p className="mb-2 text-sm font-medium">{t("Pending invitations")}</p>
          {invites.filter((i) => i.status === "pending").map((i) => (
            <div key={i.id} className="flex items-center justify-between border-t border-border py-2 first:border-0" data-testid={`invite-${i.email}`}>
              <div className="flex items-center gap-2 text-sm"><Mail size={14} className="text-muted-foreground" />{i.email}<span className="text-xs text-muted-foreground">{t("expires")}{" "}{fmtDate(i.expires_at)}</span></div>
              <Button variant="ghost" size="sm" onClick={() => revokeInvite(i.id)} data-testid={`revoke-invite-${i.email}`}>{t("Revoke")}</Button>
            </div>
          ))}
        </Card>
      )}
      {loading ? <LoadingBlock /> : (
        <Card className="overflow-hidden" data-testid="users-table">
          <div className="hidden grid-cols-12 gap-3 border-b border-border bg-muted/50 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground md:grid">
            <div className="col-span-4">{t("User")}</div>
            <div className="col-span-4">{t("Roles")}</div>
            <div className="col-span-2">{t("Status")}</div>
            <div className="col-span-2 text-right">{t("Actions")}</div>
          </div>
          {users.map((u) => (
            <div key={u.id} data-testid={`user-row-${u.email}`}
              className="grid grid-cols-1 items-center gap-2 border-b border-border px-4 py-3 last:border-0 md:grid-cols-12">
              <div className="md:col-span-4">
                <div className="font-medium">{u.name}</div>
                <div className="text-xs text-muted-foreground">{u.email}</div>
              </div>
              <div className="md:col-span-4 flex flex-wrap gap-1">
                {u.roles.map((r) => (
                  <span key={r} className="rounded bg-secondary px-2 py-0.5 text-xs capitalize text-secondary-foreground">{r.replace(/_/g, " ")}</span>
                ))}
              </div>
              <div className="md:col-span-2"><StatusPill status={u.status} /></div>
              <div className="md:col-span-2 md:text-right">
                <Button variant="ghost" size="sm" onClick={() => toggleStatus(u)} data-testid={`toggle-status-${u.email}`}>
                  <Power size={14} className="mr-1" /> {u.status === "active" ? t("Deactivate") : t("Activate")}
                </Button>
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>)
  );
}
